import React, { useMemo, useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import styles from './TemplateCard.module.scss';

export interface TemplateData {
  id: string | number;
  title: string;
  languagesCount: number;
  difficulty: string;
  img_path?: string;
  onReview?: (id: string | number) => void;
}

interface TemplateCardProps {
  data: TemplateData;
  isPublished?: boolean;
}

const PUBLIC_PLACEHOLDER_IMG = process.env.PUBLIC_URL
  ? `${process.env.PUBLIC_URL}/placeholderImage.png`
  : '/placeholderImage.png';

export const TemplateCard: React.FC<TemplateCardProps> = ({ data, isPublished = false }) => {
  const { t } = useTranslation();

  const [imgSrc, setImgSrc] = useState<string>(
    data.img_path ? data.img_path : PUBLIC_PLACEHOLDER_IMG
  );

  useEffect(() => {
    if (data.img_path) {
      setImgSrc(data.img_path);
    } else {
      setImgSrc(PUBLIC_PLACEHOLDER_IMG);
    }
  }, [data.img_path]);

  const difficultyLevel = useMemo(() => {
    const diff = data.difficulty?.toLowerCase().trim();
    if (diff === 'beginner') return 'beginner';
    if (diff === 'advanced') return 'advanced';
    return 'intermediate';
  }, [data.difficulty]);

  const handleReviewClick = () => {
    if (data.onReview) {
      data.onReview(data.id);
    }
  };

  return (
    <div
      data-button="Primary"
      data-container="logo"
      className={styles.cardContainer}
    >
      <div className={styles.imageBanner}>
        <div className={styles.badgePositionWrapper}>
          <div
            className={`${styles.difficultyBadge} ${styles[difficultyLevel]}`}
          >
            <span
              className={`${styles.difficultyText} ${styles[difficultyLevel]}`}
            >
              {data.difficulty}
            </span>
          </div>
        </div>

        <img
          src={imgSrc}
          alt={data.title}
          onError={() => setImgSrc(PUBLIC_PLACEHOLDER_IMG)}
          className={styles.thumbnailImage}
        />
      </div>

      <div className={styles.infoSection}>
        <div className={styles.templateTitle} title={data.title}>
          {data.title}
        </div>
        <div className={styles.languagesCount}>
          {data.languagesCount}{' '}
          {data.languagesCount === 1
            ? t('phishingTemplateManager.language')
            : t('phishingTemplateManager.languages')}
        </div>
      </div>

      <button
        type="button"
        onClick={handleReviewClick}
        className={styles.reviewButton}
      >
        <span className={styles.buttonText}>
          {isPublished
            ? t('phishingTemplateManager.details')
            : t('phishingTemplateManager.review')}
        </span>
      </button>
    </div>
  );
};

export default TemplateCard;